import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTutorial } from '../../context/TutorialContext';

const TOOLTIP_WIDTH = 340;
const TOOLTIP_GAP = 16;
const SPOTLIGHT_PADDING = 10;

// ─── Hook : suit le rect d'un élément data-tutorial ──────────────────────────
function useTargetRect(targetId) {
    const [rect, setRect] = useState(null);

    useEffect(() => {
        if (!targetId) { setRect(null); return; }

        const update = () => {
            const el = document.querySelector(`[data-tutorial="${targetId}"]`);
            if (el) {
                const r = el.getBoundingClientRect();
                setRect({ top: r.top, left: r.left, width: r.width, height: r.height, bottom: r.bottom, right: r.right });
            } else {
                setRect(null);
            }
        };

        update();
        window.addEventListener('resize', update);
        window.addEventListener('scroll', update, true);
        const iv = setInterval(update, 300);
        return () => { window.removeEventListener('resize', update); window.removeEventListener('scroll', update, true); clearInterval(iv); };
    }, [targetId]);

    return rect;
}

// ─── Barre de progression ─────────────────────────────────────────────────────
function ProgressBar({ current, total }) {
    return (
        <div className="flex items-center gap-1.5">
            {Array.from({ length: total }).map((_, i) => (
                <div key={i} style={{
                    width: i === current ? '22px' : '7px',
                    height: '7px',
                    borderRadius: '4px',
                    transition: 'all 0.25s ease',
                    background: i < current
                        ? 'var(--color-primary, #7c3aed)'
                        : i === current
                            ? 'var(--color-primary, #7c3aed)'
                            : '#e5e7eb',
                    opacity: i < current ? 0.45 : 1,
                }} />
            ))}
        </div>
    );
}

// ─── Modale centrée (welcome / done) ─────────────────────────────────────────
function CenteredModal({ currentStep, currentStepIndex, totalSteps, isLast, onSkip, onAdvance, onComplete }) {
    return createPortal(
        <div className="fixed inset-0 z-[9998] flex items-center justify-center" style={{ background: 'rgba(0,0,0,0.6)', pointerEvents: 'all' }}>
            <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-md mx-4 overflow-hidden">

                {/* Bandeau coloré en haut */}
                <div className="h-1.5 w-full" style={{ background: 'linear-gradient(90deg, var(--color-primary, #7c3aed), var(--color-primary-hover, #6d28d9))' }} />

                <div className="p-8 text-center">
                    <div className="text-5xl mb-4">{isLast ? '🎉' : '👋'}</div>

                    <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100 mb-2">
                        {currentStep.title}
                    </h2>
                    <p className="text-sm text-gray-500 dark:text-gray-400 leading-relaxed mb-6">
                        {currentStep.description}
                    </p>

                    <div className="flex justify-center mb-7">
                        <ProgressBar current={currentStepIndex} total={totalSteps} />
                    </div>

                    <div className="flex items-center justify-center gap-3">
                        {!isLast && (
                            <button onClick={onSkip} className="px-4 py-2 text-sm text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 transition rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700">
                                Passer
                            </button>
                        )}
                        <button
                            onClick={isLast ? onComplete : onAdvance}
                            className="px-6 py-2.5 text-sm font-semibold rounded-lg transition text-black dark:text-white hover:opacity-90"
                            style={{ background: 'var(--color-primary, #7c3aed)' }}
                        >
                            {isLast ? 'Terminer' : 'Commencer le tutoriel →'}
                        </button>
                    </div>
                </div>
            </div>
        </div>,
        document.body
    );
}

// ─── Carte flottante non-bloquante (steps blockUI: false) ─────────────────────
// Affichée en bas à droite, sans panneau d'ombre. Juste un halo sur la cible.
function FloatingHintCard({ rect, currentStep, currentStepIndex, totalSteps, onSkip, onAdvance }) {
    return createPortal(
        <>
            {/* Halo pulsé sur l'élément cible (pointer-events: none, ne bloque rien) */}
            {rect && (
                <div style={{
                    position: 'fixed',
                    top: `${rect.top - SPOTLIGHT_PADDING}px`,
                    left: `${rect.left - SPOTLIGHT_PADDING}px`,
                    width: `${rect.width + SPOTLIGHT_PADDING * 2}px`,
                    height: `${rect.height + SPOTLIGHT_PADDING * 2}px`,
                    border: '2px solid var(--color-primary, #7c3aed)',
                    borderRadius: '10px',
                    boxShadow: '0 0 0 4px rgba(124,58,237,0.18)',
                    zIndex: 9998,
                    pointerEvents: 'none',
                    animation: 'tutorialPulse 2s ease-in-out infinite',
                }} />
            )}

            {/* Carte flottante bas-droite */}
            <div style={{
                position: 'fixed',
                bottom: '24px',
                right: '24px',
                width: `${TOOLTIP_WIDTH}px`,
                zIndex: 9999,
                pointerEvents: 'all',
            }}>
                <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl overflow-hidden border border-gray-100 dark:border-gray-700">
                    {/* Bandeau coloré */}
                    <div className="h-1 w-full" style={{ background: 'linear-gradient(90deg, var(--color-primary, #7c3aed), var(--color-primary-hover, #6d28d9))' }} />

                    <div className="p-4">
                        {/* Entête */}
                        <div className="flex items-center justify-between mb-2 gap-2">
                            <div className="flex items-center gap-2">
                                <span className="text-xs font-semibold uppercase tracking-wide" style={{ color: 'var(--color-primary, #7c3aed)' }}>
                                    Tutoriel · {currentStepIndex + 1}/{totalSteps}
                                </span>
                            </div>
                            <button onClick={onSkip} className="text-gray-300 hover:text-gray-500 dark:hover:text-gray-300 text-lg leading-none flex-shrink-0" aria-label="Passer">
                                &times;
                            </button>
                        </div>

                        <h3 className="font-bold text-gray-900 dark:text-gray-100 text-sm mb-1">
                            {currentStep.title}
                        </h3>
                        <p className="text-xs text-gray-500 dark:text-gray-400 leading-relaxed mb-3">
                            {currentStep.description}
                        </p>

                        {/* Hint */}
                        {currentStep.hint && (
                            <div className="flex items-start gap-2 text-xs font-medium rounded-lg px-3 py-2 mb-3" style={{ background: 'rgba(124,58,237,0.08)', color: 'var(--color-primary, #7c3aed)' }}>
                                <span className="flex-shrink-0 mt-0.5">💡</span>
                                <span>{currentStep.hint}</span>
                            </div>
                        )}

                        {/* Bas : progress + bouton */}
                        <div className="flex items-center justify-between">
                            <ProgressBar current={currentStepIndex} total={totalSteps} />
                            {!currentStep.requiredAction && (
                                <button
                                    onClick={onAdvance}
                                    className="text-xs font-semibold px-3 py-1.5 rounded-lg transition hover:opacity-85"
                                    style={{ background: 'var(--color-primary, #7c3aed)', color: '#fff' }}
                                >
                                    Suivant →
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* Keyframe pour le halo pulsé */}
            <style>{`
                @keyframes tutorialPulse {
                    0%, 100% { box-shadow: 0 0 0 4px rgba(124,58,237,0.18); }
                    50% { box-shadow: 0 0 0 8px rgba(124,58,237,0.08); }
                }
            `}</style>
        </>,
        document.body
    );
}

// ─── Spotlight bloquant (steps blockUI: true avec target) ────────────────────
function SpotlightTooltip({ rect, currentStep, currentStepIndex, totalSteps, onSkip, onAdvance }) {
    const sTop = rect.top - SPOTLIGHT_PADDING;
    const sLeft = rect.left - SPOTLIGHT_PADDING;
    const sWidth = rect.width + SPOTLIGHT_PADDING * 2;
    const sHeight = rect.height + SPOTLIGHT_PADDING * 2;
    const sBottom = sTop + sHeight;

    const vw = window.innerWidth;
    const vh = window.innerHeight;

    // Tooltip: en dessous si assez de place, sinon au-dessus
    const tooltipTop = sBottom + TOOLTIP_GAP + 200 < vh
        ? sBottom + TOOLTIP_GAP
        : Math.max(8, sTop - TOOLTIP_GAP - 200);

    let tooltipLeft = rect.left + rect.width / 2 - TOOLTIP_WIDTH / 2;
    tooltipLeft = Math.max(12, Math.min(tooltipLeft, vw - TOOLTIP_WIDTH - 12));

    return createPortal(
        <>
            {/* Panneaux bloquants */}
            <div style={{ position: 'fixed', inset: 0, top: 0, left: 0, right: 0, height: `${sTop}px`, background: 'rgba(0,0,0,0.55)', zIndex: 9998, pointerEvents: 'all' }} />
            <div style={{ position: 'fixed', top: `${sBottom}px`, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.55)', zIndex: 9998, pointerEvents: 'all' }} />
            <div style={{ position: 'fixed', top: `${sTop}px`, left: 0, width: `${sLeft}px`, height: `${sHeight}px`, background: 'rgba(0,0,0,0.55)', zIndex: 9998, pointerEvents: 'all' }} />
            <div style={{ position: 'fixed', top: `${sTop}px`, left: `${sLeft + sWidth}px`, right: 0, height: `${sHeight}px`, background: 'rgba(0,0,0,0.55)', zIndex: 9998, pointerEvents: 'all' }} />

            {/* Bordure spotlight */}
            <div style={{
                position: 'fixed',
                top: `${sTop}px`, left: `${sLeft}px`,
                width: `${sWidth}px`, height: `${sHeight}px`,
                border: '2px solid var(--color-primary, #7c3aed)',
                borderRadius: '10px',
                zIndex: 9999,
                pointerEvents: 'none',
                boxShadow: '0 0 0 4px rgba(124,58,237,0.2)',
            }} />

            {/* Tooltip */}
            <div style={{ position: 'fixed', top: `${tooltipTop}px`, left: `${tooltipLeft}px`, width: `${TOOLTIP_WIDTH}px`, zIndex: 9999, pointerEvents: 'all' }}>
                <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl overflow-hidden border border-gray-100 dark:border-gray-700">
                    {/* Bandeau coloré */}
                    <div className="h-1 w-full" style={{ background: 'linear-gradient(90deg, var(--color-primary, #7c3aed), var(--color-primary-hover, #6d28d9))' }} />

                    <div className="p-4">
                        {/* Entête */}
                        <div className="flex items-center justify-between mb-2 gap-2">
                            <span className="text-xs font-semibold uppercase tracking-wide" style={{ color: 'var(--color-primary, #7c3aed)' }}>
                                Tutoriel · {currentStepIndex + 1}/{totalSteps}
                            </span>
                            <button onClick={onSkip} className="text-gray-300 hover:text-gray-500 dark:hover:text-gray-300 text-lg leading-none flex-shrink-0" aria-label="Passer">
                                &times;
                            </button>
                        </div>

                        <h3 className="font-bold text-gray-900 dark:text-gray-100 text-sm mb-1">
                            {currentStep.title}
                        </h3>
                        <p className="text-xs text-gray-500 dark:text-gray-400 leading-relaxed mb-3">
                            {currentStep.description}
                        </p>

                        {/* Hint */}
                        {currentStep.hint && (
                            <div className="flex items-start gap-2 text-xs font-medium rounded-lg px-3 py-2 mb-3" style={{ background: 'rgba(124,58,237,0.08)', color: 'var(--color-primary, #7c3aed)' }}>
                                <span className="flex-shrink-0 mt-0.5">👆</span>
                                <span>{currentStep.hint}</span>
                            </div>
                        )}

                        {/* Bas : progress + indicateur action */}
                        <div className="flex items-center justify-between">
                            <ProgressBar current={currentStepIndex} total={totalSteps} />
                            {currentStep.requiredAction ? (
                                <span className="text-xs text-gray-400 dark:text-gray-500 italic">Action requise</span>
                            ) : (
                                <button
                                    onClick={onAdvance}
                                    className="text-xs font-semibold px-3 py-1.5 rounded-lg transition hover:opacity-85"
                                    style={{ background: 'var(--color-primary, #7c3aed)', color: '#fff' }}
                                >
                                    Suivant →
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </>,
        document.body
    );
}

// ─── Composant principal ──────────────────────────────────────────────────────
export default function TutorialOverlay() {
    const { isActive, currentStep, currentStepIndex, totalSteps, skip, advanceStep, complete } = useTutorial();
    const rect = useTargetRect(currentStep?.target);
    const fallbackRect = useTargetRect(currentStep?.fallbackTarget ?? null);

    if (!isActive || !currentStep) return null;

    const isLastStep = currentStepIndex === totalSteps - 1;

    // Etape sans cible : modale centrée (welcome / done)
    if (!currentStep.target) {
        return (
            <CenteredModal
                currentStep={currentStep}
                currentStepIndex={currentStepIndex}
                totalSteps={totalSteps}
                isLast={isLastStep}
                onSkip={skip}
                onAdvance={advanceStep}
                onComplete={complete}
            />
        );
    }

    // Etape non-bloquante : carte flottante + halo (l'éditeur reste accessible)
    // Si la cible principale n'est pas visible, on utilise le fallbackTarget comme halo
    if (!currentStep.blockUI) {
        return (
            <FloatingHintCard
                rect={rect ?? fallbackRect}
                currentStep={currentStep}
                currentStepIndex={currentStepIndex}
                totalSteps={totalSteps}
                onSkip={skip}
                onAdvance={advanceStep}
            />
        );
    }

    // Etape bloquante avec cible : attendre que l'élément soit dans le DOM
    if (!rect) return null;

    return (
        <SpotlightTooltip
            rect={rect}
            currentStep={currentStep}
            currentStepIndex={currentStepIndex}
            totalSteps={totalSteps}
            onSkip={skip}
            onAdvance={advanceStep}
        />
    );
}
